import { Store, Star } from 'lucide-react';
import { Card } from '@atoms/Card';
import { DEFAULT_WHOLESALER_IMAGE } from '@/constants/marketingAssets';
import { mediaUrl } from '@/utils/mediaUrl';
import type { User } from '@/types';

interface WholesalerCardProps {
  wholesaler: User;
  onClick?: () => void;
  rank?: number;
  variant?: 'compact' | 'featured';
}

export function WholesalerCard({ wholesaler, onClick, rank, variant = 'compact' }: WholesalerCardProps) {
  const logo = mediaUrl(wholesaler.logo);
  const imageSrc = logo ?? DEFAULT_WHOLESALER_IMAGE;
  const featured = variant === 'featured';

  if (featured) {
    return (
      <Card
        hover
        elevated
        padding="none"
        className="relative overflow-hidden border-slate-200"
        onClick={onClick}
      >
        {rank != null && rank <= 3 && (
          <span className="absolute left-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-lg bg-slate-900 text-xs font-bold text-white shadow-sm">
            {rank}
          </span>
        )}
        <div className="aspect-[16/9] overflow-hidden bg-slate-100">
          <img
            src={imageSrc}
            alt={wholesaler.name}
            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
          />
        </div>
        <div className="p-5">
          <h3 className="truncate text-base font-semibold text-slate-900">{wholesaler.name}</h3>
          <p className="mt-0.5 truncate text-sm text-slate-500">
            {[wholesaler.district, wholesaler.city].filter(Boolean).join(', ')}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
              <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
              {(wholesaler.avgRating ?? 0).toFixed(1)}
            </span>
            <span className="text-xs text-slate-500">{wholesaler.orderCount ?? 0} sipariş</span>
          </div>
          <p className="mt-3 text-sm font-semibold text-brand-600">Mağazayı Gör →</p>
        </div>
      </Card>
    );
  }

  return (
    <Card hover elevated padding="lg" className="relative border-slate-200" onClick={onClick}>
      {rank != null && rank <= 3 && (
        <span className="absolute -left-2 -top-2 z-10 flex h-7 w-7 items-center justify-center rounded-lg bg-slate-900 text-xs font-bold text-white shadow-sm">
          {rank}
        </span>
      )}
      <div className="flex items-start gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          {logo ? (
            <img src={logo} alt="" className="h-full w-full object-cover" />
          ) : (
            <Store className="h-6 w-6 text-slate-400" strokeWidth={1.5} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-slate-900">{wholesaler.name}</h3>
          <p className="mt-0.5 truncate text-sm text-slate-500">
            {[wholesaler.district, wholesaler.city].filter(Boolean).join(', ')}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
              <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
              {(wholesaler.avgRating ?? 0).toFixed(1)}
            </span>
            <span className="text-xs text-slate-500">{wholesaler.orderCount ?? 0} sipariş</span>
          </div>
          <p className="mt-3 text-sm font-semibold text-brand-600">Mağazayı Gör →</p>
        </div>
      </div>
    </Card>
  );
}
